import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    containerless,
    customElement,
    observable
} from "aurelia-framework";
import nameof from "../../common/nameof";
import { IPermissionBehavior, IReadOnlyIfBehavior } from "../../interfaces/form-builder/i-behavior";
import { IDisplayDependency, IElement } from "../../interfaces/form-builder/i-element";
import { IAncestorSettings } from "../../interfaces/form-builder/i-input-options";
import { IInput } from "../../interfaces/form-builder/i-input";
import { FormModelType } from "../../interfaces/form-builder/i-model-schema";
import { BehaviorTypesEnum } from "./behavior-handlers/behavior-types-enum";
import { DependencyTypeEnum } from "./behavior-handlers/display-type-enum";
import { PermissionBehaviorHandler } from "./behavior-handlers/permission-behavior-handler";
import { ElementTypesEnum } from "./element-types-enum";
import { FormButtonEvent } from "./form-input/form-button/form-button";
import { FormBuilderEvent, NoteDataManager } from "./note-data-manager";
import { ReadOnlyIfBehaviorHandler } from "./behavior-handlers/read-only-if-behavior-handler";
import { FormInput } from "./form-input/form-input";

@containerless
@autoinject
@customElement("form-element")
export class FormElement {
    @bindable({ defaultBindingMode: bindingMode.toView })
    @observable({ changeHandler: nameof<FormElement>("elementChanged") })
    public element: IElement;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public parentName: string;
    @bindable({ defaultBindingMode: bindingMode.toView })
    public ancestorSettings: IAncestorSettings;
    // For Addendum, new bindable prop called skeletonElement
    // If skeletonElement exists, then get value from metaSchema and store it in this.element
    private _ea: EventAggregator;
    private _noteDataManager: NoteDataManager;
    private _permissionBehaviorHandler: PermissionBehaviorHandler;
    private _eaSubscriptions: Subscription[] = [];
    private _hasDependencies: boolean = false;
    private _hasDisplayDependencies: boolean = false;
    private _readOnlyIfBehaviorHandler: ReadOnlyIfBehaviorHandler;
    public showElement: boolean = true;
    public elementTypesEnum: typeof ElementTypesEnum = ElementTypesEnum;
    public permissionBehaviorInfo: IPermissionBehavior;
    public readOnlyIfBehaviorInfo: IReadOnlyIfBehavior;

    @computedFrom(
        `${nameof<FormElement>("element")}.${nameof<IElement>("isReadOnly")}`,
        nameof<FormElement>("ancestorSettings")
    )
    public get descendentAncestorSettings(): IAncestorSettings {
        let isReadOnly = this.ancestorSettings?.isReadOnly ?? false;
        if (
            this.element.isReadOnly &&
            (this.element.elementType === ElementTypesEnum.Grouping ||
                (this.element.elementType == ElementTypesEnum.Input &&
                    (this.element as IInput).inputType === "list-group"))
        ) {
            isReadOnly = true;
        }

        return {
            isReadOnly: isReadOnly,
            isUserToggled: this.ancestorSettings?.isUserToggled
        } as IAncestorSettings;
    }

    public constructor(
        ea: EventAggregator,
        noteDataManager: NoteDataManager,
        permissionBehaviorHandler: PermissionBehaviorHandler,
        readOnlyIfBehaviorHandler: ReadOnlyIfBehaviorHandler
    ) {
        this._ea = ea;
        this._noteDataManager = noteDataManager;
        this._permissionBehaviorHandler = permissionBehaviorHandler;
        this._readOnlyIfBehaviorHandler = readOnlyIfBehaviorHandler;
    }

    public bind() {
        if (!this.ancestorSettings) {
            this.ancestorSettings = {
                isReadOnly: false,
                isUserToggled: false
            };
        }
        this.elementChanged();
    }

    public elementChanged() {
        if (!!this.element) {
            this._hasDependencies = this.element.dependencies?.length > 0;

            this.permissionBehaviorInfo = this.element.behaviors?.find((behavior) => {
                return behavior.behaviorType === BehaviorTypesEnum.Permission;
            }) as IPermissionBehavior;

            this._hasDisplayDependencies = this.element.valueDependencies?.length > 0;
            if (this._hasDependencies || this._hasDisplayDependencies || !!this.permissionBehaviorInfo?.isShowHide) {
                this.setShowElement();
            }
            this.disposeSubscription();
            this._eaSubscriptions.push(
                this._ea.subscribe(
                    FormButtonEvent.ToggleDisplay,
                    (dependencyInfo: { elementName: string; value: FormModelType }) => {
                        if (this._hasDependencies || this._hasDisplayDependencies) {
                            let hasMyDisplayDependency = false;
                            let hasMyDependency = false;
                            if (this._hasDependencies) {
                                hasMyDependency = this.element.dependencies.some(
                                    (dependency) => dependency === dependencyInfo.elementName
                                );
                            }
                            if (this._hasDisplayDependencies) {
                                hasMyDisplayDependency = this.element.valueDependencies.some(
                                    (dependency) => dependency.targetName === dependencyInfo.elementName
                                );
                            }
                            if (hasMyDependency || hasMyDisplayDependency) {
                                this.setShowElement(dependencyInfo);
                            }
                        }
                    }
                )
            );

            this._eaSubscriptions.push(
                this._ea.subscribe(FormBuilderEvent.ModifiedDataUpdated, (prop: string) => {
                    if (this._hasDependencies || this._hasDisplayDependencies) {
                        let hasDependency = this.modifiedDataUpdated(prop);
                        if (hasDependency) {
                            let modifiedData = this._noteDataManager.getModifiedData();
                            let dependencyInfo = {
                                elementName: prop,
                                value: modifiedData[prop] as number
                            };
                            this.setShowElement(dependencyInfo);
                        }
                    }
                })
            );

            this._eaSubscriptions.push(
                this._ea.subscribe(FormBuilderEvent.ModifiedDataPropDeleted, (prop: string) => {
                    if (this._hasDependencies || this._hasDisplayDependencies) {
                        let hasDependency = this.modifiedDataUpdated(prop);
                        if (hasDependency) {
                            this.setShowElement();
                        }
                    }
                })
            );

            if (this.showElement) {
                let hasPermission = true;
                if (!!this.permissionBehaviorInfo && !this.permissionBehaviorInfo.isShowHide) {
                    hasPermission = this._permissionBehaviorHandler.handle(this.permissionBehaviorInfo);
                    if (!hasPermission) {
                        this.setReadonly(true);
                    }
                }

                if (hasPermission) {
                    // Read only behavior works if the user has permission to edit field, else its always read only
                    this.readOnlyIfBehaviorInfo = this.element.behaviors?.find((behavior) => {
                        return behavior.behaviorType === BehaviorTypesEnum.ReadOnlyIf;
                    }) as IReadOnlyIfBehavior;
                    if (!!this.readOnlyIfBehaviorInfo) {
                        this.initReadOnlyIfSubscription();
                    }
                }
            }
        }
    }

    private modifiedDataUpdated(prop: string) {
        let hasMyDisplayDependency = false;
        let hasMyDependency = false;
        if (this._hasDependencies) {
            hasMyDependency = this.element.dependencies.some((dependency) => dependency === prop);
        }
        if (this._hasDisplayDependencies) {
            hasMyDisplayDependency = this.element.valueDependencies.some(
                (dependency) => dependency.targetName === prop
            );
        }
        return hasMyDependency || hasMyDisplayDependency;
    }

    private setShowElement(dependencyInfo?: { elementName: string; value: FormModelType }) {
        let wasHidden = !this.showElement;
        if (this.permissionBehaviorInfo?.isShowHide) {
            let hasPermission = this._permissionBehaviorHandler.handle(this.permissionBehaviorInfo);
            if (!hasPermission) {
                this.showElement = false;
                return;
            }
        }
        let showElement = true;
        if (this._hasDisplayDependencies) {
            showElement = showElement && this.handleDisplayDependencies(dependencyInfo);
        } else if (this._hasDependencies) {
            showElement = showElement && this.handleDependencies(dependencyInfo);
        }
        this.showElement = showElement;

        if (wasHidden && this.showElement) {
            this._ea.publish(`${FormButtonEvent.Unhidden}:${this.element.name}`);
        }
    }

    private handleDisplayDependencies(dependencyInfo?: { elementName: string; value: FormModelType }) {
        let showElement = true;
        let displayDependencies = this.element?.valueDependencies;
        if (displayDependencies?.length > 0) {
            let independentDependencies: IDisplayDependency[] = [];
            let linkedDependencies: IDisplayDependency[] = [];
            displayDependencies.forEach((dep) =>
                !!dep.isIndependent ? independentDependencies.push(dep) : linkedDependencies.push(dep)
            );
            // This should be shown if ANY Independent is Valid OR ALL regular are Valid
            showElement =
                (independentDependencies.length > 0 &&
                    independentDependencies.some((inDep) => this.isDependencyValid(inDep, dependencyInfo))) ||
                (linkedDependencies.length > 0 &&
                    !linkedDependencies.some((liDep) => !this.isDependencyValid(liDep, dependencyInfo)));
        }
        return showElement;
    }

    private isDependencyValid(
        dependency: IDisplayDependency,
        dependencyInfo?: { elementName: string; value: FormModelType }
    ) {
        // TODO: Handle array type
        let targetName = this._noteDataManager.getModelKeyFromTargetName(dependency.targetName);
        let dependencyValue = this._noteDataManager.getValue(targetName) || false;
        // INFO: Unable to get it from modifiedData due to race condition.
        // EventAggregator is faster than data binding updates.
        if (
            !!dependencyInfo &&
            (!targetName ||
                dependencyInfo.elementName == targetName ||
                dependencyInfo.elementName == dependency.targetName)
        ) {
            dependencyValue = dependencyInfo.value;
        }
        switch (dependency.dependencyType) {
            case DependencyTypeEnum.HasValue:
                let affectingValues = dependency.values;
                affectingValues = affectingValues.map((value) => value.toString());
                if (affectingValues?.length > 0) {
                    let elementHasValue = !!this._noteDataManager.getValue(this.element.name);
                    let hasValue = affectingValues.includes(dependencyValue != null ? dependencyValue.toString() : "");
                    if (dependency.ifValueDisplay && elementHasValue) {
                        hasValue = true;
                    }
                    return hasValue;
                }
                break;
            case DependencyTypeEnum.WithInRange:
                return dependencyValue >= dependency.minValue && dependencyValue <= dependency.maxValue;
            case DependencyTypeEnum.IsNotEmpty:
                return !!dependencyValue || (Array.isArray(dependencyValue) && dependencyValue.length > 0);
            case DependencyTypeEnum.DoesNotHaveValue:
                let values = dependency.values;
                values = values.map((value) => value.toString());
                if (values?.length > 0) {
                    return values.indexOf(dependencyValue != null ? dependencyValue.toString() : "") === -1;
                }
                break;
            case DependencyTypeEnum.LimitOptions:
                let element = this.element as IInput;
                if (element.elementType === ElementTypesEnum.Input && element?.options) {
                    return !!Array.isArray(dependencyValue) && !!dependencyValue?.length;
                }
                break;
        }
        return true;
    }

    private handleDependencies(dependencyInfo?: { elementName: string; value: FormModelType }) {
        let showElement = true;
        let dependencies = this.element?.dependencies;
        if (dependencies?.length > 0) {
            let noteModel = this._noteDataManager.getNoteModel();
            showElement = dependencies.reduce((result: boolean, dependency: string) => {
                let dependencyValue = !!noteModel[dependency];
                // INFO: Unable to get it from modifiedData due to race condition.
                // EventAggregator is faster than data binding updates.
                if (!!dependencyInfo) {
                    dependencyValue = dependencyInfo.value as boolean;
                }
                return result && dependencyValue;
            }, true);
        }
        return showElement;
    }

    private async initReadOnlyIfSubscription() {
        if (!this.readOnlyIfBehaviorInfo || this.element.isReadOnly || this.ancestorSettings.isReadOnly) {
            return;
        }
        this.setReadonly(
            await this._readOnlyIfBehaviorHandler.handle(this.readOnlyIfBehaviorInfo, this._noteDataManager)
        );
        // Dynamic Read only changes is only available when the target is a field in the form
        if (!this.readOnlyIfBehaviorInfo.targetName) {
            return;
        }
        this._eaSubscriptions.push(
            this._ea.subscribe(FormBuilderEvent.ModifiedDataUpdated, async (prop: string) => {
                if (prop === this.readOnlyIfBehaviorInfo?.targetName) {
                    this.setReadonly(
                        await this._readOnlyIfBehaviorHandler.handle(this.readOnlyIfBehaviorInfo, this._noteDataManager)
                    );
                }
            })
        );
        this._eaSubscriptions.push(
            this._ea.subscribe(
                FormButtonEvent.ToggleDisplay,
                async (dependencyInfo: { elementName: string; value: FormModelType }) => {
                    if (dependencyInfo.elementName === this.readOnlyIfBehaviorInfo.targetName) {
                        this.setReadonly(
                            await this._readOnlyIfBehaviorHandler.handle(
                                this.readOnlyIfBehaviorInfo,
                                this._noteDataManager,
                                dependencyInfo
                            )
                        );
                    }
                }
            )
        );
        this._eaSubscriptions.push(
            this._ea.subscribe(FormBuilderEvent.ModifiedDataPropDeleted, async (prop: string) => {
                if (prop === this.readOnlyIfBehaviorInfo?.targetName) {
                    this.setReadonly(
                        await this._readOnlyIfBehaviorHandler.handle(this.readOnlyIfBehaviorInfo, this._noteDataManager)
                    );
                }
            })
        );
    }

    private setReadonly(isReadOnly: boolean) {
        this.element.isReadOnly = isReadOnly;
    }

    private disposeSubscription() {
        this._eaSubscriptions?.forEach((subscription) => {
            subscription?.dispose();
        });
    }

    public detached() {
        this.disposeSubscription();
    }
}
